'use client'

import { createContext, useContext, useState, type ReactNode } from 'react'

/** Where the visitor is searching from — set by the hero/footer search. */
export interface LocatorOrigin {
    lat: number
    lng: number
    /** Display label, e.g. "Boston" or "your location". */
    label: string
}

interface LocatorContextValue {
    origin: LocatorOrigin | null
    setOrigin: (origin: LocatorOrigin) => void
}

const LocatorContext = createContext<LocatorContextValue | null>(null)

export function useLocator(): LocatorContextValue {
    const ctx = useContext(LocatorContext)
    if (!ctx) {
        throw new Error('useLocator must be used within LocatorProvider')
    }
    return ctx
}

/**
 * Shares the searched-from origin between the hero search, the footer-recap
 * search, and the theater list/map. Server-rendered sections pass through as
 * children; only the search boxes and the locator grid are client islands.
 */
export const LocatorProvider = ({ children }: { children: ReactNode }) => {
    const [origin, setOrigin] = useState<LocatorOrigin | null>(null)
    return (
        <LocatorContext.Provider value={{ origin, setOrigin }}>{children}</LocatorContext.Provider>
    )
}
